// State of one upload package on SCR-01 (sad §4 item 1, §6 Flow 6). Pure: no React, no network.

export const MAX_PACKAGE_FILES = 20;

export const ROW = {
  PROCESSING: "processing",
  RECOGNIZED: "recognized",
  UNRECOGNIZED: "unrecognized",
  INVALID: "invalid",
  RATE_LIMITED: "rate-limited",
  TOO_LARGE: "too-large",
  TRANSPORT: "transport",
};

const REJECTED = new Set([ROW.UNRECOGNIZED, ROW.INVALID, ROW.RATE_LIMITED, ROW.TOO_LARGE, ROW.TRANSPORT]);
const RETRYABLE = new Set([ROW.RATE_LIMITED, ROW.TRANSPORT]);

const INVALID_CODES = new Set([
  "broker_report.structurally_invalid",
  "broker_report.reversed_period",
  "broker_report.invalid_start_date",
  "broker_report.invalid_end_date",
]);

// screens.md §SCR-01 «Стани рядка заходу». Anything not named there — network, 5xx, an encryption-key
// failure or an unexpected 4xx — is a transport failure: retryable, never mixed with a content class.
export function classifyUploadResponse(status, code) {
  if (status === 413) return ROW.TOO_LARGE;
  if (status === 429) return ROW.RATE_LIMITED;
  if (code === "broker_report.not_recognized") return ROW.UNRECOGNIZED;
  if (INVALID_CODES.has(code)) return ROW.INVALID;
  return ROW.TRANSPORT;
}

export const initialState = { rows: [], limitError: false, list: "loading", nextClientId: 1 };

export const actions = {
  filesSelected: (files) => ({ type: "filesSelected", files: Array.from(files) }),
  uploadStarted: (clientId) => ({ type: "uploadStarted", clientId }),
  uploadSucceeded: (clientId, report) => ({ type: "uploadSucceeded", clientId, report }),
  uploadFailed: (clientId, failure) => ({ type: "uploadFailed", clientId, failure }),
  retry: (clientId) => ({ type: "retry", clientId }),
  rowRemoved: (clientId) => ({ type: "rowRemoved", clientId }),
  serverListRequested: () => ({ type: "serverListRequested" }),
  serverListFailed: () => ({ type: "serverListFailed" }),
  serverReportsLoaded: (reports) => ({ type: "serverReportsLoaded", reports }),
  serverListReplaced: (reports) => ({ type: "serverListReplaced", reports }),
  removeStarted: (reportId) => ({ type: "removeStarted", reportId }),
  removeConflict: (reportId) => ({ type: "removeConflict", reportId }),
  removeAborted: (reportId) => ({ type: "removeAborted", reportId }),
  reportRemoved: (reportId) => ({ type: "reportRemoved", reportId }),
};

const isRecognized = (row) => row.state === ROW.RECOGNIZED;

function updateRow(state, matches, update) {
  const index = state.rows.findIndex(matches);
  if (index === -1) return state;
  const next = update(state.rows[index]);
  if (next === state.rows[index]) return state;
  const rows = [...state.rows];
  rows[index] = next;
  return { ...state, rows };
}

const byClientId = (clientId) => (row) => row.clientId === clientId;
const byReportId = (reportId) => (row) => isRecognized(row) && row.report.id === reportId;

function appendReports(state, reports) {
  const known = new Set(state.rows.filter(isRecognized).map((row) => row.report.id));
  let { nextClientId } = state;
  const added = reports
    .filter((report) => !known.has(report.id))
    .map((report) => ({
      clientId: nextClientId++,
      fileName: report.fileName,
      state: ROW.RECOGNIZED,
      report,
    }));
  return { ...state, rows: [...state.rows, ...added], nextClientId, list: "loaded" };
}

export function uploadPackageReducer(state, action) {
  switch (action.type) {
    case "filesSelected": {
      if (action.files.length === 0) return state;
      if (state.rows.length + action.files.length > MAX_PACKAGE_FILES) {
        return { ...state, limitError: true };
      }
      let { nextClientId } = state;
      const added = action.files.map((file) => ({
        clientId: nextClientId++,
        fileName: file.name,
        file,
        state: ROW.PROCESSING,
        active: false,
      }));
      return { ...state, rows: [...state.rows, ...added], nextClientId, limitError: false };
    }
    case "uploadStarted":
      return updateRow(state, byClientId(action.clientId), (row) =>
        row.state === ROW.PROCESSING ? { ...row, active: true } : row,
      );
    case "uploadSucceeded": {
      const updated = updateRow(state, byClientId(action.clientId), (row) => ({
        clientId: row.clientId,
        fileName: row.fileName,
        state: ROW.RECOGNIZED,
        report: action.report,
      }));
      if (updated === state) return state;
      // The server list may have delivered this id before the own 201: keep the own row only.
      const rows = updated.rows.filter(
        (row) => row.clientId === action.clientId || !byReportId(action.report.id)(row),
      );
      return { ...updated, rows };
    }
    case "uploadFailed": {
      const { failure } = action;
      const rowState = failure.transport ? ROW.TRANSPORT : classifyUploadResponse(failure.status, failure.code);
      return updateRow(state, byClientId(action.clientId), (row) => ({
        ...row,
        state: rowState,
        active: false,
        error: failure,
      }));
    }
    case "retry":
      return updateRow(state, byClientId(action.clientId), (row) =>
        RETRYABLE.has(row.state) ? { ...row, state: ROW.PROCESSING, active: false, error: undefined } : row,
      );
    case "rowRemoved": {
      const row = state.rows.find(byClientId(action.clientId));
      if (!row || !REJECTED.has(row.state)) return state;
      return { ...state, rows: state.rows.filter((r) => r !== row), limitError: false };
    }
    case "serverListRequested":
      return { ...state, list: "loading" };
    case "serverListFailed":
      return { ...state, list: "error" };
    case "serverReportsLoaded":
      return appendReports(state, action.reports);
    case "serverListReplaced": {
      const present = new Set(action.reports.map((report) => report.id));
      const kept = state.rows.filter((row) => !isRecognized(row) || present.has(row.report.id));
      return appendReports({ ...state, rows: kept }, action.reports);
    }
    case "removeStarted":
      return updateRow(state, byReportId(action.reportId), (row) => ({ ...row, removingReport: true }));
    case "removeConflict":
      return updateRow(state, byReportId(action.reportId), (row) => ({ ...row, removingReport: false, removeReportBlocked: true }));
    case "removeAborted":
      return updateRow(state, byReportId(action.reportId), (row) => ({ ...row, removingReport: false }));
    case "reportRemoved":
      return { ...state, rows: state.rows.filter((row) => !byReportId(action.reportId)(row)) };
    default:
      return state;
  }
}

// Gate of «Синхронізувати» (AC-09, sad §6 Flow 2 + Flow 6): rejected rows block first, then rows
// without an answer, then an unread server list (AC-12), then an empty package.
export function selectSyncGate(state) {
  const blockers = state.rows.filter((row) => REJECTED.has(row.state)).map((row) => row.fileName);
  const pendingCount = state.rows.filter((row) => row.state === ROW.PROCESSING).length;
  const readyReportIds = [...new Set(state.rows.filter(isRecognized).map((row) => row.report.id))];

  let kind = "ready";
  if (blockers.length > 0) kind = "blocked";
  else if (pendingCount > 0) kind = "processing";
  else if (state.list === "loading") kind = "loading";
  else if (state.list === "error") kind = "list-error";
  else if (readyReportIds.length === 0) kind = "empty";

  return { kind, blockers, pendingCount, readyReportIds };
}

export function selectCanAddFiles(state) {
  return state.rows.length < MAX_PACKAGE_FILES;
}
